import { lazy, Suspense } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useSessionReplayWindow } from '@/shared/analytics/useSessionReplayWindow';
import { useDeclarationsFreshness } from '../hooks/useDeclarationsFreshness';
import { splitPeriod } from '../lib/periods';
import { annualFromSlug, payrollFromSlug, periodFromSlug, yearFromSlug } from '../lib/paths';
import { SplitPaneContext, useIsDesktop } from '../hooks/useSplitPane';
import { useScrollMemory } from '../hooks/useScrollMemory';
import { useMarkBackOnPopState } from '../hooks/useTransitionNavigate';
import TrackingScope from '../components/TrackingScope';
import DeclarationsSyncGate from './DeclarationsSyncGate';

const YearView = lazy(() => import('./DeclarationsNewYearView'));

const DeclarationsSplitLayout = () => {
	useDeclarationsFreshness();
	const desktop = useIsDesktop();
	const { period: slug } = useParams();
	useSessionReplayWindow('declarations_new');
	useMarkBackOnPopState();
	useScrollMemory();
	if (!desktop)
		return (
			<div
				data-hide-scrollbar
				data-page-transition
				className='w-full'>
				<TrackingScope value={{ split_pane: false }}>
					<DeclarationsSyncGate>
						<Outlet />
					</DeclarationsSyncGate>
				</TrackingScope>
			</div>
		);
	const selectedPeriod = periodFromSlug(slug) ?? payrollFromSlug(slug) ?? undefined;
	const annualYear = annualFromSlug(slug);
	const year = yearFromSlug(slug) ?? annualYear ?? (selectedPeriod ? splitPeriod(selectedPeriod).year : undefined);
	return (
		<TrackingScope value={{ split_pane: true }}>
			<DeclarationsSyncGate>
				<div
					data-hide-scrollbar
					className='grid w-full grid-cols-[minmax(0,26rem)_minmax(0,1fr)] items-start'>
					<aside className='sticky top-0 max-h-dvh overflow-y-auto [scrollbar-width:none]'>
						<Suspense fallback={null}>
							<YearView
								year={year}
								selectedPeriod={selectedPeriod}
								annualSelected={annualYear !== null}
							/>
						</Suspense>
					</aside>
					<section
						data-page-transition
						className='border-h-neutral-94 min-h-dvh min-w-0 border-l'>
						<SplitPaneContext.Provider value>
							<Outlet />
						</SplitPaneContext.Provider>
					</section>
				</div>
			</DeclarationsSyncGate>
		</TrackingScope>
	);
};

export default DeclarationsSplitLayout;
